import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import {
  MyTrainingDetailCertificatesDocument,
  MyTrainingUploadCertificateModalDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NewFileModal } from 'modules/trainings/components/Files/NewFileModal'
import type { FileSubmissionData } from 'modules/trainings/components/Files/NewFileModal/types'
import { useTrainingMemberships } from 'modules/trainings/hooks/TrainingMemberships/useTrainingMemberships'

export default function UploadCertificateModal() {
  const { trainingId } = useParams<{
    trainingId: string
  }>()
  const { access, employee, company } = useCurrentSession()
  const { trainingMemberships } = useTrainingMemberships({
    trainingId: Number(trainingId),
    employeeId: employee.id,
  })
  const trainingMembership = trainingMemberships.find(
    (membership) =>
      membership.trainingId === Number(trainingId) && membership.employeeId === employee.id
  )
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [uploadCertificate] = useMutation(MyTrainingUploadCertificateModalDocument, {
    extractorFn: (data) => data.trainingsMutations.bulkCreateTrainingsTrainingMembershipFile,
    awaitRefetchQueries: true,
    refetchQueries: [MyTrainingDetailCertificatesDocument],
  })
  const navigateBack = () =>
    navigate(
      navigation.myTrainings.revamp.detailsRevamp.view.certificates.fullPath({
        trainingId: Number(trainingId),
      })
    )

  return (
    <NewFileModal
      fileTypes={['image', 'document']}
      noTitleField
      title={i18n.t('trainings.my_training.upload_certificate.title')}
      description={i18n.t('trainings.my_training.upload_certificate.description')}
      onClose={navigateBack}
      onSubmit={async (data: FileSubmissionData) => {
        const result = await uploadCertificate({
          variables: {
            authorId: access.id,
            companyId: company.id,
            trainingMembershipId: Number(trainingMembership?.id),
            files: [data.file],
          },
        })
        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('trainings.session_access_membership_table.upload_files_error'),
          })
        })
        if (handled.success) {
          navigateBack()
        }
      }}
      resourceType="Trainings::TrainingMembership"
      resourceId={trainingId}
    />
  )
}
